/**
 * Parking disputes (/parking/disputes) — V0.3 marquee Tier 1.
 *
 * Card-list of slot disputes raised by residents. Admin resolves with notes
 * or marks invalid. Optional photo upload (multipart) per dispute.
 *
 * Backend contracts:
 *   GET    /v1/parking/disputes?status=…&cursor=…&limit=…
 *   POST   /v1/parking/disputes                       (resident-side; admin can also create)
 *   POST   /v1/parking/disputes/:id/resolve           body { resolution_notes, status? }
 *   POST   /v1/parking/disputes/:id/photo             multipart file upload
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  AlertTriangle,
  ImageIcon,
  CheckCircle2,
  Ban,
  Upload,
  ArrowLeft
} from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { get, post, getErrorMessage, api } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TabsPills } from '@/components/ui/tabs-pills';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatDateTime } from '@/lib/format';
import type { ParkingDispute, DisputeStatus, CursorList } from './_parking-types';
import { DISPUTE_STATUS_BADGE } from './_parking-types';

const STATUS_LABEL: Record<DisputeStatus, string> = {
  open: 'Open',
  resolved: 'Resolved',
  invalid: 'Invalid'
};

const ResolveSchema = z.object({
  resolution_notes: z.string().trim().min(3, 'Notes required').max(1000)
});
type ResolveForm = z.infer<typeof ResolveSchema>;

type Tab = 'open' | 'resolved' | 'invalid' | 'all';

export function ParkingDisputesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('open');

  const filtersHash = useMemo(() => JSON.stringify([tab]), [tab]);
  const [cursor, setCursor] = useState<string | null>(null);
  useEffect(() => {
    setCursor(null);
  }, [filtersHash]);

  const listQ = useQuery({
    queryKey: ['parking-disputes', tab, cursor],
    queryFn: () =>
      get<CursorList<ParkingDispute>>('/parking/disputes', {
        params: {
          limit: 50,
          ...(tab !== 'all' ? { status: tab } : {}),
          ...(cursor ? { cursor } : {})
        }
      })
  });
  const paginator = useCursorAccumulator<ParkingDispute>(listQ.data, cursor, filtersHash);
  const disputes = paginator.items;

  // Resolve / mark invalid dialog.
  const [resolveTarget, setResolveTarget] = useState<{ d: ParkingDispute; status: 'resolved' | 'invalid' } | null>(null);
  const resolveForm = useForm<ResolveForm>({
    resolver: zodResolver(ResolveSchema),
    mode: 'onChange',
    defaultValues: { resolution_notes: '' }
  });
  useEffect(() => {
    if (resolveTarget) resolveForm.reset({ resolution_notes: '' });
  }, [resolveTarget]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolveDispute = useMutation({
    mutationFn: (v: ResolveForm) => {
      if (!resolveTarget) throw new Error('No dispute');
      return post<ParkingDispute>(`/parking/disputes/${resolveTarget.d.id}/resolve`, {
        resolution_notes: v.resolution_notes,
        status: resolveTarget.status
      });
    },
    onSuccess: () => {
      toast.success('Dispute updated');
      setResolveTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['parking-disputes'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // Photo upload (admin can attach if missing).
  const uploadPhoto = useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/parking/disputes/${id}/photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success('Photo uploaded');
      void queryClient.invalidateQueries({ queryKey: ['parking-disputes'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // Photo preview modal.
  const [previewPhoto, setPreviewPhoto] = useState<{ id: string; ref: string } | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!previewPhoto) {
      setPreviewUrl(null);
      return;
    }
    let revoked = false;
    let blobUrl: string | null = null;
    api
      .get<Blob>(`/parking/disputes/${previewPhoto.id}/photo`, { responseType: 'blob' })
      .then((res) => {
        if (revoked) return;
        blobUrl = URL.createObjectURL(res.data);
        setPreviewUrl(blobUrl);
      })
      .catch(() => {
        if (!revoked) setPreviewUrl(null);
      });
    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [previewPhoto]);

  return (
    <>
      <PageHeader
        title="Parking disputes"
        description="Resident-raised slot conflicts. Resolve with notes or mark invalid."
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/parking' })} title="Back to parking">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
        }
      />

      <div className="mx-auto w-full max-w-[1200px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        <Card>
          <CardContent className="flex items-center gap-vt-2 p-vt-3">
            <TabsPills
              value={tab}
              onChange={(v) => setTab(v as Tab)}
              options={[
                { value: 'open', label: 'Open' },
                { value: 'resolved', label: 'Resolved' },
                { value: 'invalid', label: 'Invalid' },
                { value: 'all', label: 'All' }
              ]}
              aria-label="Dispute status filter"
            />
          </CardContent>
        </Card>

        {listQ.isLoading ? (
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        ) : listQ.isError ? (
          <Card>
            <CardContent>
              <EmptyState
                icon={<AlertTriangle className="h-6 w-6" />}
                title="Could not load disputes"
                description={getErrorMessage(listQ.error)}
                action={
                  <Button variant="secondary" onClick={() => void listQ.refetch()}>
                    Try again
                  </Button>
                }
              />
            </CardContent>
          </Card>
        ) : disputes.length === 0 ? (
          <Card>
            <CardContent>
              <EmptyState
                icon={<AlertTriangle className="h-6 w-6" />}
                title="No disputes in this view"
                description="Resident-raised parking complaints will show here."
              />
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            {disputes.map((d) => (
              <Card key={d.id}>
                <CardContent className="flex flex-col gap-vt-3 p-vt-4">
                  <div className="flex items-start justify-between gap-vt-3">
                    <div className="min-w-0">
                      <div className="text-vt-12 text-vt-text-muted">
                        Raised by{' '}
                        <span className="text-vt-text">
                          {d.raised_by_name ?? '—'}
                          {d.raised_by_unit?.number ? ` · Unit ${d.raised_by_unit.number}` : ''}
                        </span>
                      </div>
                      <h4 className="mt-vt-1 text-vt-14 font-semibold text-vt-text">
                        {d.slot
                          ? `Slot ${d.slot.slot_number}${d.slot.zone?.name ? ` · ${d.slot.zone.name}` : ''}`
                          : 'No slot linked'}
                      </h4>
                    </div>
                    <Badge variant={DISPUTE_STATUS_BADGE[d.status]}>{STATUS_LABEL[d.status]}</Badge>
                  </div>
                  <p className="whitespace-pre-wrap text-vt-13 text-vt-text">{d.description}</p>
                  {d.photo_ref ? (
                    <button
                      type="button"
                      onClick={() => setPreviewPhoto({ id: d.id, ref: d.photo_ref! })}
                      className="inline-flex h-16 w-16 items-center justify-center rounded-vt-sm border border-vt-border bg-vt-surface-2 text-vt-text-muted transition-colors hover:bg-vt-surface-3"
                      title="Preview photo"
                      aria-label="Preview dispute photo"
                    >
                      <ImageIcon className="h-6 w-6" strokeWidth={1.5} />
                    </button>
                  ) : null}
                  {d.status === 'resolved' && d.resolution_notes ? (
                    <div className="rounded-vt-sm border border-vt-border bg-vt-surface-2/60 p-vt-2 text-vt-12 text-vt-text-muted">
                      <span className="font-semibold text-vt-text">Resolution: </span>
                      {d.resolution_notes}
                    </div>
                  ) : null}
                  <div className="flex flex-wrap items-center justify-between gap-vt-2 border-t border-vt-border pt-vt-2">
                    <span className="text-vt-11 text-vt-text-muted">
                      {formatDateTime(d.created_at)}
                    </span>
                    {d.status === 'open' ? (
                      <div className="flex items-center gap-vt-2">
                        <label className="inline-flex">
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) uploadPhoto.mutate({ id: d.id, file: f });
                              e.target.value = '';
                            }}
                          />
                          <Button size="sm" variant="ghost" asChild>
                            <span>
                              <Upload className="h-4 w-4" /> Photo
                            </span>
                          </Button>
                        </label>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setResolveTarget({ d, status: 'invalid' })}
                        >
                          <Ban className="h-4 w-4" /> Invalid
                        </Button>
                        <Button size="sm" onClick={() => setResolveTarget({ d, status: 'resolved' })}>
                          <CheckCircle2 className="h-4 w-4" /> Resolve
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <CursorPaginator
          hasMore={Boolean(paginator.nextCursor)}
          loading={listQ.isFetching}
          itemsShown={disputes.length}
          pageSize={50}
          onLoadMore={() => {
            if (paginator.nextCursor) setCursor(paginator.nextCursor);
          }}
        />
      </div>

      {/* Resolve / invalid dialog */}
      <Dialog open={Boolean(resolveTarget)} onOpenChange={(v) => (v ? null : setResolveTarget(null))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {resolveTarget?.status === 'invalid' ? 'Mark dispute invalid' : 'Resolve dispute'}
            </DialogTitle>
            <DialogDescription>
              {resolveTarget?.status === 'invalid'
                ? 'Flag this complaint as not actionable. Add notes for the audit trail.'
                : 'Capture how the conflict was resolved so future audits have the story.'}
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={resolveForm.handleSubmit((v) => resolveDispute.mutate(v))}
            className="flex flex-col gap-vt-3"
          >
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="rd-notes">Notes</Label>
              <Textarea
                id="rd-notes"
                rows={4}
                placeholder="e.g. Slot reassigned to original owner after deed verification."
                {...resolveForm.register('resolution_notes')}
              />
              {resolveForm.formState.errors.resolution_notes ? (
                <p className="text-vt-12 text-vt-alert">
                  {resolveForm.formState.errors.resolution_notes.message}
                </p>
              ) : null}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setResolveTarget(null)}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant={resolveTarget?.status === 'invalid' ? 'secondary' : 'primary'}
                loading={resolveDispute.isPending}
                loadingText="Saving…"
                disabled={resolveDispute.isPending || !resolveForm.formState.isValid}
              >
                {resolveTarget?.status === 'invalid' ? 'Mark invalid' : 'Resolve'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Photo preview */}
      <Dialog open={Boolean(previewPhoto)} onOpenChange={(v) => (v ? null : setPreviewPhoto(null))}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Dispute photo</DialogTitle>
          </DialogHeader>
          <div className="flex max-h-[70vh] items-center justify-center bg-vt-surface-2/60">
            {previewUrl ? (
              <img src={previewUrl} alt="Dispute" className="max-h-[70vh] w-auto object-contain" />
            ) : (
              <Skeleton className="h-64 w-full" />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
